/**
 * The "vMix studio" templates (docs/vmix.md): a show style and a studio for
 * one vMix, driven by TSR's vMix driver over its TCP API (the `vmix` device,
 * `ACP_VMIX` in the device layer). The second template pair beside the
 * simulators' (`studioTemplate.ts`); `acp show-style-template --kind vmix`,
 * `acp studio-template --kind vmix` and `pnpm dev:studio --vmix` use them.
 *
 * D32 split: the SHOW STYLE holds the piece types, written against abstract
 * layers whose content is TSR's vMix timeline content
 * (`TimelineContentVMix*`: `type` PROGRAM / INPUT / OVERLAY); the STUDIO holds
 * the device (host, port) and maps each layer to a vMix mapping — the
 * programme of mix 1, an input by number, an overlay channel. Which vMix
 * input plays a clip or carries a title is the studio's (`VMIX_INPUTS`, the
 * order docs/vmix.md tells the maintainer to create them in); a camera's
 * input number is the piece's param.
 *
 *   camera        programme ← input `{{input}}`, transition `{{transition}}` over `{{duration}}` ms
 *   clip / clip-b VT A / VT B: the clip input restarts and plays (layer `vmix_vt_a` / `vmix_vt_b`),
 *                 programme ← that input by a cut (`inputLayer`: whatever input the studio maps there;
 *                 TSR's vMix driver always cuts to a layer's input)
 *   lower-third   the title's fields (`vmix_lt_text`: Headline ← name, Description ← title), and
 *                 overlay 1 ← the title input (`VMIX_INPUTS.lowerThird`: the input the studio maps
 *                 `vmix_lt_text` to — the two must agree, docs/vmix.md)
 *   fullscreen    the full-screen title's Headline, programme ← its input
 *
 * Deliberate limits (PLAN §13's backlog): no preview-bus lookahead (TSR's
 * vMix driver maps a lookahead object only on its own `<layer>_lookahead`
 * layer, which would need a PREVIEW-typed copy of every programme template);
 * a title's input number is in the studio's text mapping AND this show style's overlay/programme
 * content (TSR's overlay and a transition name an input by number); a clip's end does not auto-next by itself — its part's `expectedDuration`
 * with `autoNext` does, so a part plays exactly as long as the clip; two clip
 * parts in a row on the SAME player do not restart it (`restart` fires when
 * the piece appears), which is why there are two players.
 *
 * Pure data, no I/O, beside the schemas it must satisfy (`vmixTemplate.test.ts`).
 */
import type { JsonValue } from '@norna/domain/primitives.js';

type JsonObject = { [key: string]: JsonValue };

/**
 * vMix's TCP API port (Settings → Web Controller/TCP API; TSR's default). Its HTTP API / Web Controller (8088,
 * `http://host:8088/api`) is for a browser check only; ACP does not use it.
 */
export const VMIX_TCP_PORT = 8099;

/** The input numbers the template assumes — docs/vmix.md's setup, and the vMix fake's default inputs. */
export const VMIX_INPUTS = { camera1: 1, camera2: 2, camera3: 3, vtA: 4, vtB: 5, lowerThird: 6, fullscreen: 7 } as const;

/** The GT title fields the templates write (vMix's names: `<field>.Text`; docs/vmix.md → the title). */
export const VMIX_TITLE_FIELDS = { headline: 'Headline.Text', description: 'Description.Text' } as const;

/** vMix transition effects offered on a piece (TSR sends the effect as the function name; `Cut` is a cut, `Fade` a mix). */
export const VMIX_TRANSITIONS = ['Cut', 'Fade', 'Merge', 'Wipe', 'Zoom', 'Stinger1'] as const;

const TRANSITION_PARAMS = (effect: (typeof VMIX_TRANSITIONS)[number], durationMs: number): JsonObject => ({
  transition: { type: 'string', label: 'Transition', required: false, default: effect, enum: [...VMIX_TRANSITIONS] },
  duration: { type: 'integer', label: 'Transition duration (ms)', required: false, default: durationMs, minimum: 0, maximum: 10_000 },
});

const TRANSITION: JsonObject = { effect: '{{transition}}', duration: '{{duration}}' };

export function vmixShowStyleTemplate(): JsonObject {
  const vmix = ['vmix'];
  const clip = (label: string, layer: string): JsonObject => ({
    label,
    params: {},
    sourceLayer: 'vt',
    outputLayer: 'pgm',
    lifespan: 'part-only',
    defaultDuration: null,
    timeline: [
      { layer, content: { type: 'INPUT', playing: true, restart: true } },
      // By layer, so the studio's mapping alone says which input the player is. TSR cuts to a layer's input
      // (a transition needs the input by number), so a clip always comes up on a cut.
      { layer: 'vmix_pgm', content: { type: 'PROGRAM', inputLayer: layer } },
    ],
  });
  return {
    layers: {
      vmix_pgm: { label: 'vMix programme (mix 1)', deviceTypes: vmix },
      vmix_vt_a: { label: 'Clip player A (a vMix video input)', deviceTypes: vmix },
      vmix_vt_b: { label: 'Clip player B (a vMix video input)', deviceTypes: vmix },
      vmix_lt: { label: 'Lower third on air (a vMix overlay channel)', deviceTypes: vmix },
      vmix_lt_text: { label: 'Lower third fields (the GT title input)', deviceTypes: vmix },
      vmix_full_text: { label: 'Full-screen graphic fields (the GT title input)', deviceTypes: vmix },
    },
    sourceLayers: {
      cam: { label: 'Camera' },
      vt: { label: 'Clip' },
      lt: { label: 'Lower third' },
      full: { label: 'Full-screen graphic' },
    },
    outputLayers: {
      pgm: { label: 'Programme', isPgm: true },
      overlay: { label: 'Overlay', isPgm: false },
    },
    pieceTypes: {
      camera: {
        label: 'Camera',
        params: {
          input: { type: 'integer', label: 'vMix input', required: true, minimum: 1, maximum: 1000 },
          ...TRANSITION_PARAMS('Cut', 500),
        },
        sourceLayer: 'cam',
        outputLayer: 'pgm',
        lifespan: 'part-only',
        defaultDuration: null,
        timeline: [{ layer: 'vmix_pgm', content: { type: 'PROGRAM', input: '{{input}}', transition: TRANSITION } }],
      },
      clip: clip('Clip (VT A)', 'vmix_vt_a'),
      'clip-b': clip('Clip (VT B)', 'vmix_vt_b'),
      'lower-third': {
        label: 'Lower third',
        params: {
          name: { type: 'string', label: 'Name', required: true },
          title: { type: 'string', label: 'Title', required: false, default: '' },
        },
        sourceLayer: 'lt',
        outputLayer: 'overlay',
        lifespan: 'part-only',
        defaultDuration: null,
        timeline: [
          { layer: 'vmix_lt_text', content: { type: 'INPUT', text: { [VMIX_TITLE_FIELDS.headline]: '{{name}}', [VMIX_TITLE_FIELDS.description]: '{{title}}' } } },
          // Not a param: the fields go to the input the studio maps `vmix_lt_text` to, so the overlay must show that one.
          { layer: 'vmix_lt', content: { type: 'OVERLAY', input: VMIX_INPUTS.lowerThird } },
        ],
      },
      fullscreen: {
        label: 'Full-screen graphic',
        params: {
          headline: { type: 'string', label: 'Headline', required: false, default: '' },
          ...TRANSITION_PARAMS('Fade', 500),
        },
        sourceLayer: 'full',
        outputLayer: 'pgm',
        lifespan: 'part-only',
        defaultDuration: null,
        timeline: [
          { layer: 'vmix_full_text', content: { type: 'INPUT', text: { [VMIX_TITLE_FIELDS.headline]: '{{headline}}' } } },
          // The input `vmix_full_text` is mapped to (not a param, for the same reason as the lower third's overlay).
          { layer: 'vmix_pgm', content: { type: 'PROGRAM', input: VMIX_INPUTS.fullscreen, transition: TRANSITION } },
        ],
      },
    },
    actions: {
      'lt-off': { label: 'Lower third off', steps: [{ stop: 'lt' }] },
      wide: {
        label: 'Wide shot',
        steps: [{ stop: 'lt' }, { pieceType: 'camera', params: { input: VMIX_INPUTS.camera3, transition: 'Fade', duration: 1_000 }, mode: 'now' }],
      },
    },
    adlibPanels: [
      {
        id: 'main',
        label: 'vMix',
        items: [
          { label: 'Cam 1', pieceType: 'camera', params: { input: VMIX_INPUTS.camera1 }, hotkey: 'F1' },
          { label: 'Cam 2', pieceType: 'camera', params: { input: VMIX_INPUTS.camera2 }, hotkey: 'F2' },
          { label: 'Cam 3', pieceType: 'camera', params: { input: VMIX_INPUTS.camera3 }, hotkey: 'F3' },
          { label: 'Cam 1 (mix)', pieceType: 'camera', params: { input: VMIX_INPUTS.camera1, transition: 'Fade', duration: 500 } },
          { label: 'Lower third', pieceType: 'lower-third', params: { name: 'Guest', title: 'Studio guest' } },
          { label: 'Lower third off', action: 'lt-off', hotkey: 'F4' },
          { label: 'Wide shot', action: 'wide' },
          { label: 'VT B next', pieceType: 'clip-b', params: {}, mode: 'queue' },
        ],
      },
    ],
  };
}

/** One vMix at `host:port` (the TCP API; `VMIX_TCP_PORT` unless moved) and the mappings of the vMix show style's layers. */
export function vmixStudioTemplate(host: string, port: number): JsonObject {
  const input = (n: number, oneShot: boolean): JsonObject => ({ device: 'vmix1', lookahead: 'none', oneShot, options: { mappingType: 'input', index: String(n) } });
  return {
    devices: {
      // observe: vMix takes many TCP API clients, so a standby node may watch it (D8).
      vmix1: { type: 'vmix', standbyPolicy: 'observe', options: { host, port } },
    },
    mappings: {
      vmix_pgm: { device: 'vmix1', lookahead: 'none', oneShot: false, options: { mappingType: 'program', index: 1 } },
      // Clip playback is one-shot (D8): a promotion never restarts a playing clip.
      vmix_vt_a: input(VMIX_INPUTS.vtA, true),
      vmix_vt_b: input(VMIX_INPUTS.vtB, true),
      vmix_lt: { device: 'vmix1', lookahead: 'none', oneShot: false, options: { mappingType: 'overlay', index: 1 } },
      vmix_lt_text: input(VMIX_INPUTS.lowerThird, false),
      vmix_full_text: input(VMIX_INPUTS.fullscreen, false),
    },
    settings: { minimumTakeSpanMs: 1000 },
  };
}
